import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePageTracking } from "./hooks/usePageTracking";
import { motion } from "framer-motion";
import axios from "axios";
import { API_BASE_URL } from "./utils/api";
import {
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  Link2,
  Workflow,
  Database,
  Server,
  LayoutDashboard,
  BarChart2,
  Truck
} from "lucide-react";
import logo from "./assets/imgiconlogo.png";
import heroGraphic from "./assets/hero-graphic.png";
import dashboardPreview from "./assets/dashboard-new.png";
import attScreenshot from "./assets/screenshotattsample.png";

// --- Components ---

function SectionHeading({ title, subtitle, light = false }) {
  return (
    <div className="mb-12 md:mb-16">
      <h2 className={`text-3xl md:text-4xl font-extrabold mb-4 ${light ? "text-white" : "text-navy-900"}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`text-lg max-w-2xl ${light ? "text-steel-200" : "text-slate-600"}`}>
          {subtitle}
        </p>
      )}
      <div className={`mt-6 h-1 w-20 rounded-full ${light ? "bg-gold-500" : "bg-gold-500"}`} />
    </div>
  );
}

function DemoModal({ open, onClose, onSubmit, sending, notice }) {
  if (!open) return null;

  return (
    <div
      aria-modal="true"
      role="dialog"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <div className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" onClick={onClose} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-lg bg-white sm:rounded-2xl shadow-2xl overflow-hidden rounded-t-2xl max-h-[95vh] flex flex-col"
      >
        {/* Modal header */}
        <div className="bg-navy-900 px-5 sm:px-8 py-5 sm:py-6 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">Discuss a Project</h2>
            <p className="text-slate-400 text-xs mt-0.5">I'll respond within 24 hours</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 sm:p-8 overflow-y-auto">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
                <input name="name" required placeholder="John Smith" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all text-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Company</label>
                <input name="company" required placeholder="Acme Corp" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Work Email</label>
                <input name="email" type="email" required placeholder="john@company.com" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all text-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Phone</label>
                <input name="phone" placeholder="+61 4xx xxx xxx" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all text-sm" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">What would you like to solve?</label>
              <textarea name="message" rows={3} placeholder="Tell me about the workflow, integration or technical challenge you want to solve." className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all text-sm resize-none" />
            </div>

            {notice && (
              <div className={`px-4 py-3 rounded-xl text-sm font-medium ${notice.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                {notice.msg}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-5 py-3 text-sm font-semibold text-slate-500 hover:text-navy-900 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={sending} className="flex-1 py-3 bg-navy-900 text-white font-bold rounded-xl hover:bg-navy-800 transition-colors disabled:opacity-60 text-sm flex items-center justify-center gap-2">
                {sending ? "Sending…" : <><ArrowRight size={16} /> Send Enquiry</>}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

const MODAL_DATA = {
  integrations: {
    badge: "System Integration & APIs",
    title: "Connect the Systems Your Business Already Uses",
    subtitle: "Business applications, partners and files talking to each other through APIs, EDI, webhooks and structured files.",
    points: [
      { heading: "APIs & Webhooks", body: "Design and build APIs that connect business applications, with authentication, versioning and clear error handling." },
      { heading: "EDI & File Integrations", body: "Exchange orders, shipping notices and invoices through EDI and structured files — including partners who can only send spreadsheets." },
      { heading: "Data Validation", body: "Check incoming data against business rules before it reaches your systems, and flag what doesn't match instead of failing silently." },
      { heading: "Operational Workflows", body: "Turn integrations into working processes: who is notified, what happens on an exception, and how teams follow up." },
    ],
  },
  pipelines: {
    badge: "Data Pipelines & Automation",
    title: "Move, Transform and Check Data Automatically",
    subtitle: "Fewer manual copy-paste steps and spreadsheet reconciliations — data arrives where it's needed, in the shape it's needed.",
    points: [
      { heading: "Data Ingestion", body: "Bring in data from APIs, uploads, exports and reports on a schedule or as it arrives, keeping the original for traceability." },
      { heading: "Transformations", body: "Clean, match and reshape data — recognise report layouts, normalise statuses and link records across sources." },
      { heading: "Workflow Automation", body: "Replace repetitive manual tasks with automated steps, approvals and notifications." },
      { heading: "Monitoring & Exceptions", body: "Know when a feed stops, data looks wrong or a step fails — with clear exceptions for people to act on." },
    ],
  },
  backend: {
    badge: "Backend & Databases",
    title: "Reliable APIs and Application Services",
    subtitle: "Backend services and databases designed to be secure, maintainable and observable.",
    points: [
      { heading: "Backend Services", body: "Application APIs with input validation, rate limiting and structured error handling." },
      { heading: "Relational Data Modelling", body: "Database schemas designed around the business — clear relationships, constraints and indexes." },
      { heading: "Database Migrations", body: "Versioned schema changes so databases evolve safely alongside the application." },
      { heading: "Authentication & Operations", body: "Sign-in, roles and tenant isolation, plus audit trails and health checks for running the application day to day." },
    ],
  },
  infrastructure: {
    badge: "Infrastructure & DevOps",
    title: "Run Services on Infrastructure You Control",
    subtitle: "Servers, containers, self-hosted services, deployment workflows and monitoring.",
    points: [
      { heading: "Servers & Containers", body: "Containerised services defined as configuration, so environments are repeatable and easy to rebuild." },
      { heading: "Self-Hosted Backend Services", body: "APIs and databases running on your own hardware, with automated deployments from version control." },
      { heading: "Secure Remote Access", body: "Publish services securely without opening inbound ports, and reach private systems over a private network." },
      { heading: "Application Health Checks", body: "Health endpoints, service checks and dashboards that show what is running — and say so clearly when something isn't." },
    ],
  },
  applications: {
    badge: "Applications & Interfaces",
    title: "Web Applications Connected to Real Data",
    subtitle: "Web applications, admin dashboards and internal tools built on top of real APIs.",
    points: [
      { heading: "Web Applications", body: "Component-based interfaces that are fast, maintainable and consistent across the product." },
      { heading: "Responsive Interfaces", body: "Layouts that work on desktop, tablet and mobile — for office teams and people on the floor." },
      { heading: "Operational Dashboards", body: "Live views of orders, shipments, KPIs and exceptions, with filters and drill-downs that match how teams work." },
      { heading: "API-Connected Tools", body: "Internal administration tools for users, permissions, uploads and workflows — wired to backend APIs, not spreadsheets." },
    ],
  },
  dataBi: {
    badge: "Data & Business Intelligence",
    title: "Make Operational Information Actionable",
    subtitle: "Data models, dashboards and reporting that turn operational data into decisions.",
    points: [
      { heading: "Reports & Measures", body: "Business intelligence reports and measures designed around the questions people actually ask." },
      { heading: "Data Modelling", body: "Queries and data models that bring operational sources together into a consistent, trustworthy view." },
      { heading: "KPI Reporting", body: "Clear definitions for on-time performance, lead times, volumes and exceptions — measured the same way every time." },
      { heading: "Operational Visibility", body: "Dashboards that show where work is stuck and what needs attention, not just what happened last month." },
    ],
  },
  supplyChain: {
    badge: "Supply Chain Technology",
    title: "Software That Fits How Logistics Really Works",
    subtitle: "Applying software and integration skills to the realities of logistics and operations.",
    points: [
      { heading: "Process Understanding", body: "Hands-on knowledge of inbound, outbound, warehousing, carriers and customs — so solutions fit the real process." },
      { heading: "Operational Problem Solving", body: "Start from the problem on the floor — late orders, missing data, manual workarounds — and work back to the right fix." },
      { heading: "Business-Focused Delivery", body: "Small, useful increments that deliver value early, rather than long projects that deliver late." },
      { heading: "From Requirements to Solutions", body: "Translate business requirements into technical designs that teams can use, support and extend." },
    ],
  },
};

function OverlayModal({ modalKey, data, onClose, onDemo }) {
  if (!modalKey || !data) return null;
  const m = data[modalKey];
  if (!m) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-navy-900/75 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 20 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        className="relative w-full sm:max-w-2xl bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92vh]"
      >
        {/* Header — fixed */}
        <div className="bg-navy-900 px-5 sm:px-8 py-5 sm:py-6 flex items-start justify-between gap-4 shrink-0 rounded-t-2xl sm:rounded-t-2xl">
          <div className="min-w-0">
            <span className="inline-block px-2.5 py-0.5 bg-gold-500/20 border border-gold-500/30 rounded-full text-gold-400 text-[10px] font-bold uppercase tracking-widest mb-3">
              {m.badge}
            </span>
            <h3 className="text-base sm:text-xl font-extrabold text-white leading-tight">{m.title}</h3>
            <p className="text-slate-400 text-xs sm:text-sm mt-2 leading-relaxed">{m.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="mt-1 shrink-0 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-300 hover:text-white transition-all"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content — scrollable */}
        <div className="overflow-y-auto flex-1 p-5 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {m.points.map((pt, i) => (
              <div key={i} className="p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-navy-200 hover:shadow-sm transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={14} className="text-emerald-500 shrink-0" />
                  <h4 className="text-sm font-bold text-navy-900">{pt.heading}</h4>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed pl-5">{pt.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer — fixed */}
        <div className="px-5 sm:px-8 py-4 sm:pb-8 flex items-center gap-3 border-t border-slate-100 shrink-0">
          <button
            onClick={() => { onClose(); onDemo(); }}
            className="flex-1 sm:flex-none px-5 sm:px-6 py-3 bg-navy-900 text-white text-sm font-bold rounded-xl hover:bg-navy-800 transition-colors text-center"
          >
            Discuss a Project
          </button>
          <button
            onClick={onClose}
            className="flex-1 sm:flex-none px-5 sm:px-6 py-3 bg-slate-100 text-slate-600 text-sm font-semibold rounded-xl hover:bg-slate-200 transition-colors text-center"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function LandingPage() {
  usePageTracking("/");
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [activeModal, setActiveModal] = useState(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const openDemo = () => setDemoOpen(true);
  const closeDemo = () => setDemoOpen(false);

  async function submitContact(e) {
    e.preventDefault();
    setSending(true);
    setNotice(null);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await axios.post(`${API_BASE_URL}/api/demo-request`, data);
      if (response.data.ok) {
        setNotice({ ok: true, msg: "Thank you! Your project enquiry has been received. I'll be in touch within 24 hours." });
        setTimeout(() => {
          closeDemo();
          setNotice(null);
        }, 4000);
      } else {
        throw new Error(response.data.error || "Failed to send request");
      }
    } catch (err) {
      console.error("Submission error:", err);
      setNotice({ ok: false, msg: err.response?.data?.error || "Connection error. Please try again." });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-navy-900 selection:text-white">

      {/* Navigation — floating dark pill */}
      <nav className="fixed top-5 left-0 right-0 z-50 flex justify-center px-4">
        <div className={`flex items-center justify-between gap-4 px-4 py-2.5 rounded-2xl shadow-2xl shadow-black/40 border border-white/10 transition-all duration-300 w-full max-w-3xl ${isScrolled ? "backdrop-blur-xl" : "backdrop-blur-lg"}`} style={{ backgroundColor: 'rgba(26, 37, 53, 0.95)' }}>

          {/* Logo */}
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-8 h-8 rounded-full overflow-hidden border border-white/20">
              <img src={logo} alt="TinyGlobal" className="w-full h-full object-cover" />
            </div>
            <span className="text-sm font-bold text-white tracking-tight">TinyGlobal</span>
          </div>

          {/* Desktop Nav items */}
          <div className="hidden md:flex items-center gap-1">
            <a href="#platform" className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200">
              Capabilities
            </a>
            <Link to="/login" className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200">
              Client Login
            </Link>
          </div>

          {/* CTA — desktop only */}
          <button
            onClick={openDemo}
            className="hidden md:block shrink-0 px-5 py-2 bg-gold-500 text-navy-900 text-sm font-bold rounded-xl hover:bg-gold-400 transition-all duration-200 shadow-lg shadow-gold-500/20"
          >
            Discuss a Project
          </button>

          {/* Mobile Toggle */}
          <button className="md:hidden text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu — full screen overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[49] md:hidden flex flex-col" style={{ backgroundColor: '#071d2e' }}>
          {/* dot pattern */}
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="relative flex flex-col justify-center flex-1 px-8 gap-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full overflow-hidden border border-white/20">
                <img src={logo} alt="TinyGlobal" className="w-full h-full object-cover" />
              </div>
              <span className="text-white font-bold text-lg">TinyGlobal</span>
            </div>
            <a href="#platform" onClick={() => setMobileMenuOpen(false)} className="text-2xl font-bold text-white hover:text-gold-400 transition-colors">Capabilities</a>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="text-2xl font-bold text-white hover:text-gold-400 transition-colors">Client Login</Link>
            <button
              onClick={() => { openDemo(); setMobileMenuOpen(false); }}
              className="mt-4 w-full py-4 bg-gold-500 text-navy-900 font-bold text-lg rounded-2xl hover:bg-gold-400 transition-colors"
            >
              Discuss a Project
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-40 md:pb-32 bg-[#071d2e] text-white overflow-hidden" style={{ background: 'radial-gradient(circle at 30% 40%, #0b2a42, #071d2e)' }}>
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block py-1 px-3 border border-gold-500/30 rounded-full text-xs font-bold tracking-wider text-gold-400 mb-6 uppercase bg-gold-500/10">
                Supply Chain × Software × Infrastructure
              </span>
              <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6 text-white">
                Connecting Business Operations{" "}
                <br className="hidden sm:block" />
                <span className="text-gold-500">with Software & Infrastructure</span>
              </h1>
              <p className="text-lg md:text-xl text-steel-200 mb-10 leading-relaxed max-w-xl">
                I build practical technology solutions that connect systems, automate workflows and make operational data more useful — from APIs and data pipelines to backend services, databases and self-hosted infrastructure.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openDemo}
                  className="px-8 py-4 bg-gold-500 text-navy-900 font-bold rounded-lg shadow-lg shadow-gold-500/10 hover:bg-gold-400 hover:shadow-gold-500/20 transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  Discuss a Project
                </button>
                <a
                  href="#platform"
                  className="px-8 py-4 border border-white/20 text-white font-bold rounded-lg hover:bg-white/5 transition-all duration-300 text-center"
                >
                  Explore Capabilities
                </a>
              </div>

              {/* Feature Pills */}
              <div className="mt-12 pt-8 border-t border-white/10">
                <p className="text-sm font-semibold text-steel-400 uppercase tracking-widest mb-4">Capabilities</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    "✓ APIs & Integrations",
                    "✓ Data Pipelines",
                    "✓ Backend Engineering",
                    "✓ Self-Hosted Infrastructure",
                    "✓ Automation",
                    "✓ Supply Chain Systems",
                  ].map((tag) => (
                    <span key={tag} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/15 text-white/80 text-xs font-bold tracking-wide">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative mt-8 lg:mt-0">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative rounded-full aspect-square overflow-hidden shadow-2xl border border-white/10 max-w-md mx-auto"
              >
                <img src={heroGraphic} alt="Australian Logistics Network" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071d2e] via-transparent to-transparent opacity-40" />
              </motion.div>
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-gold-500/10 blur-3xl rounded-full" />
              <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-blue-500/10 blur-3xl rounded-full" />
            </div>
          </div>
        </div>
      </section>



      {/* Capabilities — card grid, each opens a detail modal */}
      <section id="platform" className="py-20 relative" style={{ background: 'radial-gradient(circle at 30% 40%, #0b2a42, #071d2e)' }}>
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-gold-400 font-bold uppercase tracking-widest text-xs mb-3 block">What I Build</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white">From Operational Problems to Working Technology</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Link2 size={24} />, color: "bg-blue-600", label: "System Integration & APIs", desc: "Connect business applications through APIs, EDI, webhooks and structured files.", modalKey: "integrations" },
              { icon: <Workflow size={24} />, color: "bg-violet-600", label: "Data Pipelines & Automation", desc: "Move, transform and validate data while reducing repetitive manual workflows.", modalKey: "pipelines" },
              { icon: <Database size={24} />, color: "bg-emerald-600", label: "Backend & Databases", desc: "Build APIs, application services and the databases behind them.", modalKey: "backend" },
              { icon: <Server size={24} />, color: "bg-amber-500", label: "Infrastructure & DevOps", desc: "Servers, containers, self-hosted services, deployment workflows and monitoring.", modalKey: "infrastructure" },
              { icon: <LayoutDashboard size={24} />, color: "bg-slate-500", label: "Applications & Interfaces", desc: "Build web applications, admin dashboards and tools connected to real APIs.", modalKey: "applications" },
              { icon: <BarChart2 size={24} />, color: "bg-teal-600", label: "Data & Business Intelligence", desc: "Use data models, dashboards and reporting to make operational information more actionable.", modalKey: "dataBi" },
              { icon: <Truck size={24} />, color: "bg-gold-500", label: "Supply Chain Technology", desc: "Apply software and integration skills to the realities of logistics and operations.", modalKey: "supplyChain" },
            ].map((btn, idx, all) => (
              <button
                key={idx}
                onClick={() => setActiveModal(btn.modalKey)}
                className={`${idx === all.length - 1 ? "sm:col-span-2 lg:col-span-1 lg:col-start-2 " : ""}group relative text-left p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/30 transition-all duration-300 overflow-hidden`}
              >
                <div className={`mb-4 w-12 h-12 ${btn.color} flex items-center justify-center text-white rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300`}>
                  {btn.icon}
                </div>
                <h3 className="text-base font-bold text-white mb-2">{btn.label}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{btn.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 group-hover:text-gold-400 transition-colors">
                  Explore <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>


      {/* Footer — floating dark pill style */}
      <footer className="bg-[#071d2e] py-10 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Main pill container */}
          <div className="bg-[#1a2535]/90 backdrop-blur-lg border border-white/10 rounded-2xl shadow-2xl shadow-black/40 p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">

              {/* Brand */}
              <div className="col-span-2 md:col-span-1">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 rounded-full overflow-hidden border border-white/20">
                    <img src={logo} alt="TinyGlobal" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-white font-bold tracking-tight">TinyGlobal</span>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed mb-4">
                  Supply chain technology, system integrations, backend engineering and infrastructure.
                </p>
                <div className="text-xs text-slate-500">
                  © 2025 TinyGlobal Pty Ltd.<br />Sydney, Australia
                </div>
                <div className="mt-3 text-xs space-y-1">
                  <a href="https://www.yadata.com.au" target="_blank" rel="noopener noreferrer" className="block text-gold-400 hover:text-gold-300 transition-colors font-semibold">yadata.com.au</a>
                  <a href="mailto:contact@yadata.com.au" className="block text-gold-400 hover:text-gold-300 transition-colors font-semibold">contact@yadata.com.au</a>
                </div>
              </div>

              {/* Platform */}
              <div>
                <h5 className="text-white font-bold mb-4 uppercase text-[10px] tracking-[0.2em]">Platform</h5>
                <ul className="space-y-2.5 text-xs text-slate-400">
                  <li><a href="#platform" className="hover:text-white transition-colors">Overview</a></li>
                  <li><a href="#integrations" className="hover:text-white transition-colors">Integrations</a></li>
                  <li><a href="#roadmap" className="hover:text-white transition-colors">Roadmap</a></li>
                </ul>
              </div>

              {/* Company */}
              <div>
                <h5 className="text-white font-bold mb-4 uppercase text-[10px] tracking-[0.2em]">Company</h5>
                <ul className="space-y-2.5 text-xs text-slate-400">
                  <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h5 className="text-white font-bold mb-4 uppercase text-[10px] tracking-[0.2em]">Legal</h5>
                <ul className="space-y-2.5 text-xs text-slate-400">
                  <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <OverlayModal
        modalKey={activeModal}
        data={MODAL_DATA}
        onClose={() => setActiveModal(null)}
        onDemo={openDemo}
      />

      <DemoModal
        open={demoOpen}
        onClose={closeDemo}
        onSubmit={submitContact}
        sending={sending}
        notice={notice}
      />
    </div>
  );
}
